import { useState, useEffect, useCallback, useMemo } from "react";
import { getLotSize } from '../useSettings';
import { useLiveData } from '../LiveDataContext';

var API = 'https://api.algrok.com';
function fmtRs(n) { if (n == null) return '--'; return (n >= 0 ? '+' : '') + Math.round(n).toLocaleString('en-IN') + '\u20B9'; }

var DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
// A time input returns HH:MM when the seconds are zero and HH:MM:SS otherwise.
// The engine stores HH:MM:SS, so normalise in both directions rather than
// slicing seconds off (which is what stopped 09:16:30 ever being saved).
function pad3(t) {
  var p = String(t || '').split(':');
  if (p.length < 2) return '09:16:00';
  var h = ('0' + (parseInt(p[0], 10) || 0)).slice(-2);
  var m = ('0' + (parseInt(p[1], 10) || 0)).slice(-2);
  var sec = ('0' + (p.length > 2 ? (parseInt(p[2], 10) || 0) : 0)).slice(-2);
  return h + ':' + m + ':' + sec;
}

var DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
var UNDERLYINGS = ['NIFTY','BANKNIFTY','FINNIFTY','SENSEX'];
var STRIKE_MODES = [
  {v:'ATM',l:'ATM'},{v:'ATM+50',l:'ATM+50'},{v:'ATM-50',l:'ATM-50'},
  {v:'ATM+100',l:'ATM+100'},{v:'ATM-100',l:'ATM-100'},
  {v:'ATM+150',l:'ATM+150'},{v:'ATM-150',l:'ATM-150'},
  {v:'ATM+200',l:'ATM+200'},{v:'ATM-200',l:'ATM-200'},
  {v:'PREMIUM',l:'By Premium'},
];

function defaultLeg() {
  return {
    option_type:'CE', buy_sell:'SELL', strike_mode:'ATM', strike_offset:0,
    premium_mode:'close_to', premium_value:200, premium_strike_filter:'none',
    qty_lots:1, sl_type:'none', sl_value:0, target_type:'none', target_value:0,
    square_off_action:'square_off_leg',
    re_entry_enabled:false, re_entry_type:'RE_ENTRY', re_entry_max:0,
  };
}

function defaultAlgo() {
  return {
    name:'', underlying:'NIFTY', segment:'Options', trade_type:'Intraday', product:'MIS',
    underlying_type:'Spot', multiplier:1, start_time:'09:16:00', end_time:'15:15:00',
    legs:[defaultLeg()],
    mtm_target_type:'none', mtm_target_value:0, mtm_target_action:'square_off_all',
    mtm_sl_type:'none', mtm_sl_value:0,
    mtm_trailing_type:'none', mtm_trailing_action:'square_off_all',
    entry_order_type:'MARKET', exit_order_type:'MARKET',
    sl_limit_buffer_type:'percentage', sl_limit_buffer_value:2, sl_limit_market_after_secs:5,
    sl_placement:'place_in_advance', sl_placement_delay_leg:0, sl_placement_delay_mtm:0,
    entry_delay:'no_delay', entry_delay_secs:0, exit_delay:'no_delay', exit_delay_secs:0,
    move_sl_to_cost:false,
    mtm_re_entry:'none', mtm_re_entry_max:0,
    re_execute_method:'LTP', re_cost_activation:'next_minute',
    trailing_frequency_mins:1, auto_sl_cancel_handling:true, auto_exit_margin_error:false,
    forward_testing:false,
    schedule:{monday:true,tuesday:true,wednesday:true,thursday:true,friday:true,saturday:false,sunday:false},
  };
}

var inp = { background:'#0a0f1e', color:'#e2e8f0', border:'1px solid rgba(26,45,74,0.8)', borderRadius:4, padding:'5px 8px', fontSize:11, fontFamily:'inherit', width:'100%' };
var lbl = { fontSize:10, color:'#8899aa', marginBottom:3, display:'block' };
var btn = function(color, bg) { return { padding:'6px 14px', borderRadius:4, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit', background:bg, color:color, border:'1px solid '+color+'44' }; };

function useWindowWidth() {
  var get = function() { return window.innerWidth; };
  var [w, setW] = useState(get());
  useEffect(function() { var h = function() { setW(get()); }; window.addEventListener('resize', h); return function() { window.removeEventListener('resize', h); }; }, []);
  return w;
}

export default function Quantiply() {
  var [view, setView] = useState('list'); // list | create | edit
  var winW = useWindowWidth();
  var isCompact = winW < 1080;
  var isTight = winW < 900;
  var [algos, setAlgos] = useState([]);
  var [editAlgo, setEditAlgo] = useState(null);
  var [selectedDay, setSelectedDay] = useState(DAY_LABELS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1]);
  var [loading, setLoading] = useState(false);
  var [totalMtm, setTotalMtm] = useState(0);
  var [dupModal, setDupModal] = useState(null);
  var [expandedAlgo, setExpandedAlgo] = useState(null); // algo id for position panel
  var [saveErrors, setSaveErrors] = useState([]);
  var { getLTP, subscribeTokens } = useLiveData();

  // Subscribe all open leg tokens for real-time LTP
  useEffect(function() {
    if (!algos || !algos.length) return;
    var tokens = [];
    algos.forEach(function(a) {
      if (a.status !== 'trade_active') return;
      (a.legs_detail || []).forEach(function(leg) {
        if (leg.token && leg.status === 'open') tokens.push(leg.token);
      });
    });
    if (tokens.length > 0) subscribeTokens(tokens);
  }, [algos]);

  // Compute live MTM per algo from WebSocket ticks
  var liveAlgos = useMemo(function() {
    if (!algos || !algos.length) return algos;
    return algos.map(function(algo) {
      if (algo.status !== 'trade_active') return algo;
      var liveMtm = 0;
      var liveLegs = (algo.legs_detail || []).map(function(leg) {
        if (leg.status !== 'open' && leg.status !== 'closed' && leg.status !== 'sl_hit' && leg.status !== 'tgt_hit') return leg;
        var tickLtp = leg.token ? getLTP(leg.token) : 0;
        var ltp = (tickLtp && tickLtp > 0) ? tickLtp : leg.ltp;
        var entry = leg.avg_entry || leg.entry_price || 0;
        var qty = leg.qty || 0;
        var pnl = leg.pnl || 0;
        if (leg.status === 'open' && ltp && entry && qty) {
          pnl = leg.buy_sell === 'BUY' ? Math.round((ltp - entry) * qty * 100) / 100 : Math.round((entry - ltp) * qty * 100) / 100;
        }
        liveMtm += pnl;
        // Include realized PnL from previous entry/exit cycles (recosts/re-entries)
        (leg.entries_history || []).forEach(function(h) { liveMtm += (h.pnl || 0); });
        return Object.assign({}, leg, { ltp: ltp || leg.ltp, pnl: pnl });
      });
      return Object.assign({}, algo, { legs_detail: liveLegs, mtm: Math.round(liveMtm * 100) / 100 });
    });
  }, [algos, getLTP]);

  var fetchAlgos = useCallback(function() {
    fetch(API + '/algos').then(function(r) { return r.json(); }).then(function(d) {
      if (Array.isArray(d)) { setAlgos(d); }
    }).catch(function() {});
  }, []);

  useEffect(function() { fetchAlgos(); var id = setInterval(fetchAlgos, expandedAlgo ? 2000 : 5000); return function() { clearInterval(id); }; }, [fetchAlgos, expandedAlgo]);

  // Live totalMtm from tick-overlaid data
  var liveTotalMtm = useMemo(function() {
    return liveAlgos.reduce(function(s, a) { return s + (a.mtm || 0); }, 0);
  }, [liveAlgos]);

  var doApi = function(url, body, cb) {
    setLoading(true);
    fetch(API + url, { method:'POST', headers:{'Content-Type':'application/json'}, body: body ? JSON.stringify(body) : undefined })
      .then(function(r) { return r.json(); })
      .then(function(d) { setLoading(false); fetchAlgos(); if (cb) cb(d); })
      .catch(function() { setLoading(false); });
  };

  var toggleAlgo = function(algo) {
    // Per-day enable/disable — algo stays on day tab, just won't trade
    var dk = DAYS[DAY_LABELS.indexOf(selectedDay)];
    var currentlyEnabled = algo.day_enabled ? algo.day_enabled[dk] === true : false;
    doApi('/algo/' + algo.id + '/toggle-day', { day: dk, enabled: !currentlyEnabled });
  };

  var updateMultiplier = function(algo, newMult) {
    doApi('/algo/' + algo.id + '/set-schedule', { multiplier: Math.max(1, newMult) });
  };

  var openDupModal = function(algo) {
    setDupModal({
      algoId: algo.id,
      name: algo.name + ' (copy)',
      days: { monday:false, tuesday:false, wednesday:false, thursday:false, friday:false, saturday:false, sunday:false },
      forwardTesting: false,
    });
  };

  var confirmDuplicate = function() {
    if (!dupModal) return;
    var schedule = {};
    DAYS.forEach(function(dk) { schedule[dk] = dupModal.days[dk] === true; });
    doApi('/algo/' + dupModal.algoId + '/dup-with-config', {
      name: dupModal.name,
      schedule: schedule,
      forward_testing: dupModal.forwardTesting,
    }, function() {
      setDupModal(null);
    });
  };

  var removeFromDay = function(algo) {
    var dk = DAYS[DAY_LABELS.indexOf(selectedDay)];
    var newSchedule = Object.assign({}, algo.schedule || {});
    newSchedule[dk] = false;
    doApi('/algo/' + algo.id + '/set-schedule', { schedule: newSchedule });
  };

  var permanentDelete = function(algo) {
    if (!window.confirm('PERMANENTLY DELETE "' + algo.name + '"?\n\nThis removes the algo from ALL days. This cannot be undone.')) return;
    doApi('/algo/' + algo.id + '/delete');
  };

  var getBuySellLabel = function(algo) {
    if (!algo.legs_detail) {
      // From summary, infer from name or default
      return 'Sell';
    }
    var hasBuy = false, hasSell = false;
    (algo.legs_detail || []).forEach(function(l) {
      if (l.buy_sell === 'BUY') hasBuy = true;
      if (l.buy_sell === 'SELL') hasSell = true;
    });
    if (hasBuy && hasSell) return 'Buy/Sell';
    if (hasBuy) return 'Buy';
    return 'Sell';
  };

  var dayKey = DAYS[DAY_LABELS.indexOf(selectedDay)];
  var dayAlgos = liveAlgos.filter(function(a) { return a.schedule && a.schedule[dayKey] === true; });
  var activeCount = dayAlgos.filter(function(a) {
    var dayOn = a.day_enabled ? a.day_enabled[dayKey] === true : false;
    return dayOn && (a.status === 'ready' || (a.status === 'trade_active' && a.activated_day === dayKey));
  }).length;

  // ── LIST VIEW ──
  if (view === 'list') {
    return (
      <div style={{ display:'flex', flexDirection:'column', height:'100%', background:'#080e1d', color:'#e2e8f0', fontFamily:"'Inter', sans-serif", overflow:'hidden' }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', background:'#0d1526', borderBottom:'1px solid rgba(26,45,74,0.6)', flexShrink:0 }}>
          <div style={{ fontSize:16, fontWeight:800 }}>Quantiply</div>
          <div style={{ fontSize:11, color:'#556677' }}>Algo Strategy Manager</div>
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:10, color:'#556677' }}>Total MTM</span>
            <span style={{ fontSize:16, fontWeight:900, color: liveTotalMtm >= 0 ? '#00d4aa' : '#ff4466', padding:'4px 12px', borderRadius:4, background: liveTotalMtm >= 0 ? 'rgba(0,212,170,0.1)' : 'rgba(255,68,102,0.1)' }}>{fmtRs(liveTotalMtm)}</span>
            <button onClick={function() { setEditAlgo(defaultAlgo()); setView('create'); }} style={btn('#0ea5e9', 'rgba(14,165,233,0.15)')}>+ Create Algo</button>
          </div>
        </div>

        {/* Day tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid rgba(26,45,74,0.4)', flexShrink:0 }}>
          {DAY_LABELS.map(function(d, i) {
            var dk = DAYS[i];
            var total = liveAlgos.filter(function(a) { return a.schedule && a.schedule[dk]; }).length;
            var enabled = liveAlgos.filter(function(a) {
              if (!a.schedule || !a.schedule[dk]) return false;
              var dayOn = a.day_enabled ? a.day_enabled[dk] === true : false;
              return dayOn;
            }).length;
            var isToday = i === (new Date().getDay() === 0 ? 6 : new Date().getDay() - 1);
            return (
              <button key={d} onClick={function() { setSelectedDay(d); }}
                style={{ flex:1, padding:'8px', fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'inherit', border:'none',
                  background: selectedDay === d ? '#0d1526' : 'transparent',
                  color: selectedDay === d ? '#0ea5e9' : isToday ? '#4ade80' : '#556677',
                  borderBottom: selectedDay === d ? '2px solid #0ea5e9' : '2px solid transparent' }}>
                {d} <span style={{ fontSize:9 }}>{enabled}/{total}</span>
                {isToday && <span style={{ color:'#4ade80', marginLeft:4 }}>{'\u25CF'}</span>}
              </button>
            );
          })}
        </div>

        {/* Column headers */}
        <div style={{ display:'flex', padding:'6px 16px', background:'#0a0f1e', borderBottom:'1px solid rgba(26,45,74,0.3)', fontSize:10, color:'#556677', fontWeight:700, flexShrink:0, alignItems:'center' }}>
          <span style={{ width:50 }}>Enable</span>
          <span style={{ width:85 }}>Status</span>
          <span style={{ flex:1 }}>Algo Name</span>
          <span style={{ width:90 }}>Multiplier</span>
          <span style={{ width:90, textAlign:'right' }}>MTM</span>
          <span style={{ width:55 }}>Start</span>
          <span style={{ width:55 }}>End</span>
          <span style={{ width:55 }}>Trade</span>
          <span style={{ width:40 }}>UL</span>
          <span style={{ width:60 }}>Side</span>
          <span style={{ width:120 }}>Actions</span>
          <span style={{ width:80, fontSize:7 }}>Algo ID</span>
        </div>

        {/* Algo rows */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {dayAlgos.length === 0 ? (
            <div style={{ padding:40, textAlign:'center', color:'#3d4f63' }}>No algos for {selectedDay}. Click "+ Create Algo" to start.</div>
          ) : dayAlgos.map(function(algo) {
            var dk = DAYS[DAY_LABELS.indexOf(selectedDay)];
            var isScheduled = algo.schedule && algo.schedule[dk];
            var isDayEnabled = algo.day_enabled ? algo.day_enabled[dk] === true : false;
            var isToday = dk === DAYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
            var isActiveToday = algo.status === 'trade_active' && algo.activated_day === dk;
            var isClosedToday = (algo.status === 'trade_closed' || algo.status === 'disabled') && algo.activated_day === dk && isToday;

            // Day-specific status: Disabled → Enabled → Ready → Trade Active → Closed
            var statusLabel, statusColor;
            if (!isDayEnabled) {
              statusLabel = 'Disabled'; statusColor = '#556677';
            } else if (!isToday) {
              statusLabel = 'Enabled'; statusColor = '#0ea5e9';
            } else if (isActiveToday) {
              statusLabel = 'Trade Active'; statusColor = '#00d4aa';
            } else if (isClosedToday) {
              statusLabel = 'Closed'; statusColor = '#ff4466';
            } else if (algo.status === 'ready') {
              statusLabel = 'Ready'; statusColor = '#fbbf24';
            } else if (isDayEnabled) {
              statusLabel = 'Enabled'; statusColor = '#0ea5e9';
            } else {
              statusLabel = 'Disabled'; statusColor = '#556677';
            }
            var isEnabled = isDayEnabled && (algo.status === 'ready' || isActiveToday);
            var sideLabel = algo.buy_sell_label || 'Sell';
            var ul = (algo.underlying || 'NIFTY').replace('NIFTY', 'NF').replace('BANKNIFTY', 'BNF').replace('FINNIFTY', 'FNF').replace('SENSEX', 'SX');
            var isExpanded = expandedAlgo === algo.id;
            return (
              <div key={algo.id}>
              <div style={{ display:'flex', alignItems:'center', padding:'7px 16px', borderBottom:'1px solid rgba(26,45,74,0.12)',
                background: isActiveToday ? 'rgba(0,212,170,0.03)' : isClosedToday ? 'rgba(255,68,102,0.02)' : algo.status === 'ready' && isToday && isDayEnabled ? 'rgba(251,191,36,0.03)' : isDayEnabled ? 'rgba(14,165,233,0.02)' : 'transparent' }}>
                {/* Toggle — per-day schedule toggle */}
                <div style={{ width:50 }}>
                  <button onClick={function() { toggleAlgo(algo); }} style={{ width:36, height:20, borderRadius:10, border:'none', cursor:'pointer',
                    background: isDayEnabled ? '#0ea5e9' : '#2a3548', position:'relative', transition:'background 0.2s' }}>
                    <div style={{ width:16, height:16, borderRadius:8, background:'white', position:'absolute', top:2,
                      left: isDayEnabled ? 18 : 2, transition:'left 0.2s' }} />
                  </button>
                </div>
                {/* Status */}
                <div style={{ width:85 }}>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:3, color:statusColor,
                    background:statusColor+'15', border:'1px solid '+statusColor+'30' }}>{statusLabel}</span>
                </div>
                {/* Algo Name — click to expand positions */}
                <div style={{ flex:1 }}>
                  <span style={{ fontSize:11, fontWeight:700, color: isExpanded ? '#fbbf24' : '#0ea5e9', cursor:'pointer' }}
                    onClick={function() { setExpandedAlgo(isExpanded ? null : algo.id); }}>
                    {isExpanded ? '\u25BC ' : '\u25B6 '}{algo.name}
                  </span>
                  {algo.forward_testing && <span style={{ fontSize:11, marginLeft:6, padding:'1px 4px', borderRadius:2, background:'rgba(251,191,36,0.15)', color:'#fbbf24' }}>FT</span>}
                  {isActiveToday && algo.open_legs > 0 && <span style={{ fontSize:11, marginLeft:6, padding:'1px 4px', borderRadius:2, background:'rgba(0,212,170,0.15)', color:'#00d4aa' }}>{algo.open_legs}L</span>}
                </div>
                {/* Multiplier — editable inline */}
                <div style={{ width:90, display:'flex', alignItems:'center', gap:2 }}>
                  <button onClick={function() { updateMultiplier(algo, (algo.multiplier || 1) - 1); }}
                    style={{ width:18, height:18, borderRadius:3, border:'1px solid rgba(26,45,74,0.4)', background:'none', color:'#8899aa', cursor:'pointer', fontSize:11, fontFamily:'inherit', lineHeight:'16px' }}>{'\u2212'}</button>
                  <span style={{ fontSize:12, fontWeight:700, color:'#e2e8f0', minWidth:24, textAlign:'center' }}>{algo.multiplier || 1}x</span>
                  <button onClick={function() { updateMultiplier(algo, (algo.multiplier || 1) + 1); }}
                    style={{ width:18, height:18, borderRadius:3, border:'1px solid rgba(26,45,74,0.4)', background:'none', color:'#8899aa', cursor:'pointer', fontSize:11, fontFamily:'inherit', lineHeight:'16px' }}>+</button>
                </div>
                {/* MTM */}
                <div style={{ width:90, textAlign:'right', fontSize:11, fontWeight:700, color: (algo.mtm||0) >= 0 ? '#00d4aa' : '#ff4466' }}>
                  {isToday && algo.mtm ? fmtRs(algo.mtm) : ''}
                </div>
                {/* Start time */}
                <div style={{ width:55, fontSize:11, color:'#8899aa' }}>{(algo.start_time||'').slice(0,8)}</div>
                {/* End time */}
                <div style={{ width:55, fontSize:11, color:'#8899aa' }}>{(algo.end_time||'').slice(0,8)}</div>
                {/* Trade type */}
                <div style={{ width:55, fontSize:11, color:'#556677' }}>Intraday</div>
                {/* Underlying */}
                <div style={{ width:40, fontSize:11, color:'#8899aa' }}>{ul}</div>
                {/* Buy/Sell */}
                <div style={{ width:60, fontSize:11, fontWeight:700, color: sideLabel === 'Sell' ? '#ff4466' : sideLabel === 'Buy' ? '#00d4aa' : '#fbbf24' }}>{sideLabel}</div>
                {/* Actions */}
                <div style={{ width:120, display:'flex', gap:3, flexWrap:'wrap' }}>
                  {isActiveToday && (
                    <button onClick={function() { doApi('/algo/' + algo.id + '/exit', { reason:'Manual' }); }}
                      style={{ padding:'2px 5px', borderRadius:3, fontSize:11, cursor:'pointer', fontFamily:'inherit', background:'rgba(255,68,102,0.1)', color:'#ff4466', border:'1px solid rgba(255,68,102,0.2)' }}>Exit</button>
                  )}
                  {!isActiveToday && isDayEnabled && isToday && (
                    <button onClick={function() { if (window.confirm('Enter ' + algo.name + ' NOW?')) doApi('/algo/' + algo.id + '/enter'); }}
                      style={{ padding:'2px 5px', borderRadius:3, fontSize:11, cursor:'pointer', fontFamily:'inherit', background:'rgba(0,212,170,0.1)', color:'#00d4aa', border:'1px solid rgba(0,212,170,0.2)', fontWeight:700 }}>{'\u26A1'}Enter</button>
                  )}
                  <button onClick={function() { fetch(API + '/algo/' + algo.id).then(function(r) { return r.json(); }).then(function(d) { if (d.id) { setEditAlgo(d); setView('edit'); } }); }}
                    style={{ padding:'2px 5px', borderRadius:3, fontSize:11, cursor:'pointer', fontFamily:'inherit', background:'rgba(14,165,233,0.1)', color:'#0ea5e9', border:'1px solid rgba(14,165,233,0.2)' }}>Edit</button>
                  <button onClick={function() { openDupModal(algo); }}
                    style={{ padding:'2px 5px', borderRadius:3, fontSize:11, cursor:'pointer', fontFamily:'inherit', background:'rgba(100,100,120,0.1)', color:'#8899aa', border:'1px solid rgba(100,100,120,0.2)' }}>Dup</button>
                  <button onClick={function() { removeFromDay(algo); }}
                    style={{ padding:'2px 5px', borderRadius:3, fontSize:11, cursor:'pointer', fontFamily:'inherit', background:'rgba(255,68,102,0.05)', color:'#ff4466', border:'1px solid rgba(255,68,102,0.15)' }}>Del</button>
                </div>
                {/* Algo ID */}
                <div style={{ width:80, fontSize:11, color:'#3d4f63', overflow:'hidden', textOverflow:'ellipsis' }}>{algo.id}</div>
              </div>

              {/* Expandable Position Panel */}
              {isExpanded && (
                <div style={{ padding:'10px 16px', background:'#0a0f1e', borderBottom:'2px solid rgba(14,165,233,0.2)' }}>
                  {!isToday ? (
                    <div style={{ padding:20, textAlign:'center', color:'#3d4f63', fontSize:11 }}>
                      No trade data for {selectedDay}. Positions and logs are available on the trading day only.
                    </div>
                  ) : algo.legs_detail && algo.legs_detail.length > 0 ? (function(){
                    // Only show legs that have actually traded (not pending)
                    var tradedLegs = algo.legs_detail.filter(function(l){return l.status !== 'pending';});
                    var hasPending = algo.legs_detail.some(function(l){return l.status === 'pending';});
                    if (!tradedLegs.length && hasPending) {
                      return <div>
                        <div style={{ padding:20, textAlign:'center', color:'#fbbf24', fontSize:11 }}>
                          {'\u23F3'} Waiting for entry at {(algo.start_time||'').slice(0,8)} — {algo.legs_count} legs configured
                        </div>
                        {algo.events && algo.events.length > 0 && (
                          <div style={{ marginTop:4, padding:'6px 8px', borderRadius:4, background:'rgba(6,10,18,0.5)', maxHeight:120, overflowY:'auto' }}>
                            <div style={{ fontSize:9, fontWeight:800, color:'#3d4f63', marginBottom:4 }}>ACTIVITY LOG ({algo.events.length})</div>
                            {algo.events.slice().reverse().map(function(ev, ei) {
                              return <div key={ei} style={{ display:'flex', gap:6, fontSize:9, marginBottom:1 }}>
                                <span style={{ color:'#3d4f63', flexShrink:0 }}>{ev.ts}</span>
                                <span style={{ color: ev.type === 'error' ? '#ff4466' : ev.type === 'success' ? '#00d4aa' : ev.type === 'warning' ? '#fbbf24' : '#6b7a8d' }}>{ev.msg}</span>
                              </div>;
                            })}
                          </div>
                        )}
                      </div>;
                    }
                    if (!tradedLegs.length) {
                      return <div style={{ color:'#3d4f63', textAlign:'center', padding:16, fontSize:11 }}>No trades yet</div>;
                    }
                    return <>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                      <thead>
                        <tr>
                          {['#', 'Side', 'Type', 'Strike', 'Symbol', 'Qty', 'Entry', 'Exit', 'LTP', 'P&L', 'Status', 'Time'].map(function(h) {
                            return <th key={h} style={{ padding:'4px 6px', textAlign:'left', color:'#556677', fontWeight:700, borderBottom:'1px solid rgba(26,45,74,0.3)', fontSize:10 }}>{h}</th>;
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {tradedLegs.map(function(leg, li) {
                          var rows = [];
                          // History rows (previous entry/exit cycles)
                          if (leg.entries_history && leg.entries_history.length > 0) {
                            leg.entries_history.forEach(function(h, hi) {
                              var hPnl = h.pnl || 0;
                              rows.push(
                                <tr key={'h'+li+'_'+hi} style={{ opacity:0.45, borderBottom:'1px solid rgba(26,45,74,0.06)', background:'rgba(13,21,38,0.3)' }}>
                                  <td style={{ padding:'3px 6px', color:'#3d4f63', fontSize:9 }}>L{li+1}</td>
                                  <td style={{ padding:'3px 6px', fontWeight:700, color: leg.buy_sell==='SELL'?'#ff4466':'#00d4aa', fontSize:10 }}>{leg.buy_sell}</td>
                                  <td style={{ padding:'3px 6px', color: leg.option_type==='CE'?'#00d4aa':'#ff4466', fontSize:10 }}>{leg.option_type}</td>
                                  <td style={{ padding:'3px 6px', color:'#8899aa', fontSize:10 }}>{leg.strike||''}</td>
                                  <td style={{ padding:'3px 6px', color:'#556677', fontSize:9 }}>{leg.symbol||''}</td>
                                  <td style={{ padding:'3px 6px', color:'#556677', fontSize:10 }}>{leg.qty||''}</td>
                                  <td style={{ padding:'3px 6px', color:'#556677', fontSize:10 }}>{h.avg_entry?'\u20B9'+h.avg_entry.toFixed(1):h.entry_price?'\u20B9'+h.entry_price.toFixed(1):'--'}</td>
                                  <td style={{ padding:'3px 6px', color:'#ff4466', fontSize:10 }}>{h.exit_price?'\u20B9'+h.exit_price.toFixed(1):'--'}</td>
                                  <td style={{ padding:'3px 6px', color:'#556677', fontSize:10 }}>--</td>
                                  <td style={{ padding:'3px 6px', fontWeight:700, color:hPnl>=0?'#00d4aa':'#ff4466', fontSize:10 }}>{hPnl?fmtRs(hPnl):'--'}</td>
                                  <td style={{ padding:'3px 6px' }}><span style={{ fontSize:9, padding:'1px 4px', borderRadius:3, color:h.status==='sl_hit'?'#ff4466':'#556677', background:'rgba(100,100,120,0.06)' }}>{h.status==='sl_hit'?'SL HIT':'exited'}</span></td>
                                  <td style={{ padding:'3px 6px', color:'#3d4f63', fontSize:9 }}>{h.entry_time||''}{h.exit_time?' \u2192 '+h.exit_time:''}</td>
                                </tr>
                              );
                            });
                          }
                          // Current entry row
                          var isOpen = leg.status === 'open';
                          var isClosed = leg.status === 'closed' || leg.status === 'sl_hit' || leg.status === 'tgt_hit';
                          var statusLabel = leg.re_label || (leg.re_cost_pending ? leg.status+' (recost pending)' : leg.status);
                          rows.push(
                            <tr key={li} style={{ opacity: isClosed ? 0.7 : 1, borderBottom:'1px solid rgba(26,45,74,0.1)' }}>
                              <td style={{ padding:'4px 6px', color:'#8899aa' }}>L{li+1}</td>
                              <td style={{ padding:'4px 6px', fontWeight:700, color: leg.buy_sell === 'SELL' ? '#ff4466' : '#00d4aa' }}>{leg.buy_sell}</td>
                              <td style={{ padding:'4px 6px', color: leg.option_type === 'CE' ? '#00d4aa' : '#ff4466' }}>{leg.option_type}</td>
                              <td style={{ padding:'4px 6px', color:'#e2e8f0', fontWeight:700 }}>{leg.strike || leg.strike_mode}</td>
                              <td style={{ padding:'4px 6px', color:'#8899aa', fontSize:10 }}>{leg.symbol || '--'}</td>
                              <td style={{ padding:'4px 6px', color:'#8899aa' }}>{leg.qty || '--'}</td>
                              <td style={{ padding:'4px 6px', color:'#8899aa' }}>{leg.avg_entry ? '\u20B9' + leg.avg_entry.toFixed(1) : '--'}</td>
                              <td style={{ padding:'4px 6px', color: isClosed ? '#ff4466' : '#3d4f63' }}>{isClosed && leg.exit_price ? '\u20B9' + leg.exit_price.toFixed(1) : '--'}</td>
                              <td style={{ padding:'4px 6px', color:'#e2e8f0', fontWeight:600 }}>{leg.ltp ? '\u20B9' + leg.ltp.toFixed(1) : '--'}</td>
                              <td style={{ padding:'4px 6px', fontWeight:700, color: (leg.pnl || 0) >= 0 ? '#00d4aa' : '#ff4466' }}>{leg.pnl ? fmtRs(leg.pnl) : '--'}</td>
                              <td style={{ padding:'4px 6px' }}>
                                <span style={{ fontSize:10, padding:'1px 5px', borderRadius:3,
                                  color: isOpen ? '#00d4aa' : leg.status==='sl_hit' ? '#ff4466' : leg.status==='tgt_hit' ? '#fbbf24' : leg.re_cost_pending ? '#fbbf24' : '#556677',
                                  background: isOpen ? 'rgba(0,212,170,0.1)' : leg.re_cost_pending ? 'rgba(251,191,36,0.08)' : 'rgba(100,100,120,0.1)' }}>{statusLabel}</span>
                                {leg.sl_mode === 'live_monitor' && isOpen && <span style={{ fontSize:8, padding:'0 3px', borderRadius:2, marginLeft:3, color:'#fbbf24', background:'rgba(251,191,36,0.1)' }}>SL:LIVE</span>}
                              </td>
                              <td style={{ padding:'4px 6px', color:'#556677', fontSize:10 }}>{leg.entry_time || ''}{leg.status !== 'open' && leg.exit_time ? ' \u2192 ' + leg.exit_time : ''}</td>
                            </tr>
                          );
                          return rows;
                        })}
                        {/* MTM total row */}
                        <tr style={{ borderTop:'2px solid rgba(26,45,74,0.4)', background:'#080e1d' }}>
                          <td colSpan={9} style={{ padding:'5px 6px', textAlign:'right', color:'#8899aa', fontWeight:700, fontSize:10 }}>TOTAL MTM</td>
                          <td style={{ padding:'5px 6px', fontWeight:900, fontSize:13, color: (algo.mtm || 0) >= 0 ? '#00d4aa' : '#ff4466' }}>{fmtRs(algo.mtm || 0)}</td>
                          <td colSpan={2} style={{ padding:'5px 6px', color:'#556677', fontSize:10 }}>{algo.open_legs} open / {algo.legs_count} total</td>
                        </tr>
                      </tbody>
                    </table>
                    {/* Activity Log */}
                    {algo.events && algo.events.length > 0 && (
                      <div style={{ marginTop:8, padding:'6px 8px', borderRadius:4, background:'rgba(6,10,18,0.5)', maxHeight:120, overflowY:'auto' }}>
                        <div style={{ fontSize:9, fontWeight:800, color:'#3d4f63', marginBottom:4 }}>ACTIVITY LOG ({algo.events.length})</div>
                        {algo.events.slice().reverse().map(function(ev, ei) {
                          return <div key={ei} style={{ display:'flex', gap:6, fontSize:9, marginBottom:1 }}>
                            <span style={{ color:'#3d4f63', flexShrink:0 }}>{ev.ts}</span>
                            <span style={{ color: ev.type === 'error' ? '#ff4466' : ev.type === 'success' ? '#00d4aa' : ev.type === 'warning' ? '#fbbf24' : '#6b7a8d' }}>{ev.msg}</span>
                          </div>;
                        })}
                      </div>
                    )}
                  </>;
                  }()) : (
                    <div style={{ color:'#3d4f63', textAlign:'center', padding:16, fontSize:11 }}>No leg data — algo hasn't traded yet</div>
                  )}
                </div>
              )}
              </div>
            );
          })}
        </div>

        {/* Duplicate Modal */}
        {dupModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100 }}
            onClick={function(e) { if (e.target === e.currentTarget) setDupModal(null); }}>
            <div style={{ background:'#0d1526', border:'1px solid rgba(26,45,74,0.6)', borderRadius:8, padding:20, width:400, fontFamily:'inherit' }}>
              <div style={{ fontSize:14, fontWeight:800, color:'#e2e8f0', marginBottom:16 }}>Duplicate Algo</div>

              <div style={{ marginBottom:12 }}>
                <label style={lbl}>Name</label>
                <input value={dupModal.name} onChange={function(e) { setDupModal(function(p) { return Object.assign({}, p, { name: e.target.value }); }); }} style={inp} />
              </div>

              <div style={{ marginBottom:12 }}>
                <label style={lbl}>Enable for days</label>
                <div style={{ display:'flex', gap:4 }}>
                  {DAY_LABELS.map(function(d, i) {
                    var dk = DAYS[i];
                    var on = dupModal.days[dk];
                    return <button key={d} onClick={function() { setDupModal(function(p) { var nd = Object.assign({}, p.days); nd[dk] = !on; return Object.assign({}, p, { days: nd }); }); }}
                      style={{ padding:'5px 10px', borderRadius:3, fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                        background: on ? 'rgba(14,165,233,0.2)' : 'rgba(26,45,74,0.2)', color: on ? '#0ea5e9' : '#3d4f63',
                        border:'1px solid ' + (on ? 'rgba(14,165,233,0.4)' : 'rgba(26,45,74,0.3)') }}>{d}</button>;
                  })}
                </div>
              </div>

              <div style={{ marginBottom:16 }}>
                <label style={lbl}>Mode</label>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={function() { setDupModal(function(p) { return Object.assign({}, p, { forwardTesting: false }); }); }}
                    style={{ flex:1, padding:'8px', borderRadius:4, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                      background: !dupModal.forwardTesting ? 'rgba(0,212,170,0.15)' : 'rgba(26,45,74,0.2)',
                      color: !dupModal.forwardTesting ? '#00d4aa' : '#556677',
                      border:'1px solid ' + (!dupModal.forwardTesting ? 'rgba(0,212,170,0.4)' : 'rgba(26,45,74,0.3)') }}>
                    {'\u25CF'} Normal (Live)
                  </button>
                  <button onClick={function() { setDupModal(function(p) { return Object.assign({}, p, { forwardTesting: true }); }); }}
                    style={{ flex:1, padding:'8px', borderRadius:4, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                      background: dupModal.forwardTesting ? 'rgba(251,191,36,0.15)' : 'rgba(26,45,74,0.2)',
                      color: dupModal.forwardTesting ? '#fbbf24' : '#556677',
                      border:'1px solid ' + (dupModal.forwardTesting ? 'rgba(251,191,36,0.4)' : 'rgba(26,45,74,0.3)') }}>
                    {'\u25CB'} Forward Test (Paper)
                  </button>
                </div>
              </div>

              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button onClick={function() { setDupModal(null); }}
                  style={{ padding:'6px 16px', borderRadius:4, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                    background:'rgba(100,100,120,0.1)', color:'#8899aa', border:'1px solid rgba(100,100,120,0.2)' }}>Cancel</button>
                <button onClick={confirmDuplicate} disabled={loading}
                  style={{ padding:'6px 16px', borderRadius:4, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                    background:'rgba(14,165,233,0.15)', color:'#0ea5e9', border:'1px solid rgba(14,165,233,0.4)',
                    opacity: loading ? 0.5 : 1 }}>{loading ? 'Creating...' : 'Create Duplicate'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── CREATE / EDIT VIEW ──
  var algo = editAlgo || defaultAlgo();
  var setA = function(key) { return function(val) { setEditAlgo(function(prev) { var n = Object.assign({}, prev || defaultAlgo()); n[key] = val; return n; }); }; };
  var setLeg = function(idx, key, val) {
    setEditAlgo(function(prev) {
      var n = Object.assign({}, prev || defaultAlgo());
      n.legs = n.legs.map(function(l, i) { if (i === idx) { var nl = Object.assign({}, l); nl[key] = val; return nl; } return l; });
      return n;
    });
  };
  var addLeg = function() { setEditAlgo(function(prev) { var n = Object.assign({}, prev); n.legs = n.legs.concat([defaultLeg()]); return n; }); };
  var removeLeg = function(idx) { setEditAlgo(function(prev) { var n = Object.assign({}, prev); n.legs = n.legs.filter(function(_, i) { return i !== idx; }); return n; }); };

  var parseOffset = function(mode) {
    if (mode === 'ATM') return 0;
    if (mode === 'PREMIUM') return 0;
    var m = mode.match(/ATM([+-]\d+)/);
    return m ? parseInt(m[1]) : 0;
  };

  var saveAlgo = function() {
    // Validate before saving
    var errors = [];
    if (!algo.name || !algo.name.trim()) errors.push('Algo name is required');
    if (!algo.legs || algo.legs.length === 0) errors.push('At least one leg is required');
    if (!algo.start_time) errors.push('Start time is required');
    if (!algo.end_time) errors.push('End time is required');
    if (algo.start_time >= algo.end_time) errors.push('Start time must be before end time');

    // Validate each leg
    (algo.legs || []).forEach(function(l, i) {
      var n = 'Leg ' + (i + 1);
      if (!l.option_type) errors.push(n + ': Option type (CE/PE) is required');
      if (!l.buy_sell) errors.push(n + ': Side (BUY/SELL) is required');
      if (l.strike_mode === 'PREMIUM') {
        if (!l.premium_value || l.premium_value <= 0) errors.push(n + ': Premium value must be > 0');
      }
      if (!l.qty_lots || l.qty_lots < 1) errors.push(n + ': Lots must be >= 1');
      if (l.sl_type !== 'none' && (!l.sl_value || l.sl_value <= 0)) errors.push(n + ': SL value must be > 0 when SL is enabled');
      if (l.target_type !== 'none' && (!l.target_value || l.target_value <= 0)) errors.push(n + ': Target value must be > 0 when target is enabled');
    });

    // MTM checks
    if (algo.mtm_target_type !== 'none' && (!algo.mtm_target_value || algo.mtm_target_value <= 0))
      errors.push('MTM Target value must be > 0 when enabled');
    if (algo.mtm_sl_type !== 'none' && (!algo.mtm_sl_value || algo.mtm_sl_value <= 0))
      errors.push('MTM SL value must be > 0 when enabled');

    // Schedule check
    var hasDay = Object.values(algo.schedule || {}).some(function(v) { return v === true; });
    if (!hasDay) errors.push('At least one day must be scheduled');

    if (errors.length > 0) {
      setSaveErrors(errors);
      return;
    }
    setSaveErrors([]);

    // Process strike offsets
    var processed = Object.assign({}, algo);
    processed.legs = algo.legs.map(function(l) {
      var nl = Object.assign({}, l);
      if (nl.strike_mode !== 'PREMIUM') {
        nl.strike_offset = parseOffset(nl.strike_mode);
        nl.strike_mode = nl.strike_mode === 'PREMIUM' ? 'PREMIUM' : 'ATM';
      }
      return nl;
    });
    var url = view === 'create' ? '/algo/create' : '/algo/' + algo.id + '/update';
    processed.lot_size = getLotSize(processed.underlying);
    doApi(url, processed, function() { setView('list'); setEditAlgo(null); });
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:'#080e1d', color:'#e2e8f0', fontFamily:"'Inter', sans-serif", overflow:'hidden' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 16px', background:'#0d1526', borderBottom:'1px solid rgba(26,45,74,0.6)', flexShrink:0 }}>
        <button onClick={function() { setView('list'); setEditAlgo(null); }} style={{ background:'none', border:'none', color:'#0ea5e9', fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>{'\u2190'}</button>
        <div style={{ fontSize:15, fontWeight:800 }}>{view === 'create' ? 'Create Algo' : 'Edit Algo'}</div>
        {view === 'edit' && <span style={{ fontSize:11, color:'#556677' }}>{algo.id}</span>}
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'16px' }}>
        {/* Basic info */}
        <div style={{ display:'grid', gridTemplateColumns:isCompact?'1fr 1fr':'1fr 1fr 1fr 1fr', gap:isCompact?8:12, marginBottom:16 }}>
          <div><label style={lbl}>Algo Name</label><input value={algo.name} onChange={function(e) { setA('name')(e.target.value); }} style={inp} placeholder="My Strategy" /></div>
          <div><label style={lbl}>Underlying</label><select value={algo.underlying} onChange={function(e) { setA('underlying')(e.target.value); }} style={inp}>{UNDERLYINGS.map(function(u) { return <option key={u} value={u}>{u}</option>; })}</select></div>
          <div><label style={lbl}>Multiplier</label><input type="number" min="1" max="50" value={algo.multiplier} onChange={function(e) { setA('multiplier')(+e.target.value); }} style={inp} /></div>
          <div><label style={lbl}>Product</label>
            <select value={algo.product || 'MIS'} onChange={function(e) { setA('product')(e.target.value); }} style={inp}>
              <option value="MIS">MIS (intraday)</option>
              <option value="NRML">NRML (carry)</option>
            </select>
          </div>
          <div><label style={lbl}>Forward Testing</label><select value={algo.forward_testing ? 'yes' : 'no'} onChange={function(e) { setA('forward_testing')(e.target.value === 'yes'); }} style={inp}><option value="no">Live Trading</option><option value="yes">Forward Test (Paper)</option></select></div>
        </div>

        {/* Time + Schedule */}
        <div style={{ display:'flex', gap:12, marginBottom:16, alignItems:'flex-end' }}>
          <div><label style={lbl}>Start Time</label><input type="time" step="1" value={pad3(algo.start_time || '09:16:00')} onChange={function(e) { setA('start_time')(pad3(e.target.value)); }} style={inp} /></div>
          <div><label style={lbl}>End Time</label><input type="time" step="1" value={pad3(algo.end_time || '15:15:00')} onChange={function(e) { setA('end_time')(pad3(e.target.value)); }} style={inp} /></div>
          <div style={{ display:'flex', gap:4 }}>
            {DAY_LABELS.map(function(d, i) {
              var dk = DAYS[i];
              var on = algo.schedule ? algo.schedule[dk] !== false : true;
              return <button key={d} onClick={function() { var s = Object.assign({}, algo.schedule || {}); s[dk] = !on; setA('schedule')(s); }}
                style={{ padding:'4px 8px', borderRadius:3, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                  background: on ? 'rgba(14,165,233,0.15)' : 'rgba(26,45,74,0.2)', color: on ? '#0ea5e9' : '#3d4f63',
                  border:'1px solid ' + (on ? 'rgba(14,165,233,0.3)' : 'rgba(26,45,74,0.3)') }}>{d.slice(0,2)}</button>;
            })}
          </div>
          <div style={{ display:'flex', gap:4, alignItems:'center' }}>
            <input type="checkbox" checked={algo.move_sl_to_cost || false} onChange={function(e) { setA('move_sl_to_cost')(e.target.checked); }} />
            <span style={{ fontSize:11, color:'#8899aa' }}>Move SL to Cost</span>
          </div>
        </div>

        {/* POSITIONS / LEGS */}
        <div style={{ marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <span style={{ fontSize:12, fontWeight:800, color:'#0ea5e9' }}>POSITIONS</span>
            <button onClick={addLeg} style={btn('#0ea5e9', 'rgba(14,165,233,0.15)')}>+ Add Leg</button>
          </div>

          {algo.legs.map(function(leg, idx) {
            return (
              <div key={idx} style={{ padding:12, marginBottom:8, borderRadius:6, background:'#0d1526', border:'1px solid rgba(26,45,74,0.4)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ fontSize:10, fontWeight:700, color:'#8899aa' }}>Leg {idx + 1}</span>
                  {algo.legs.length > 1 && <button onClick={function() { removeLeg(idx); }} style={{ fontSize:11, color:'#ff4466', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Remove</button>}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'80px 80px 120px 70px', gap:8, marginBottom:8 }}>
                  <div><label style={lbl}>Type</label><select value={leg.option_type} onChange={function(e) { setLeg(idx,'option_type',e.target.value); }} style={inp}><option value="CE">CE</option><option value="PE">PE</option></select></div>
                  <div><label style={lbl}>Side</label><select value={leg.buy_sell} onChange={function(e) { setLeg(idx,'buy_sell',e.target.value); }} style={inp}><option value="BUY">Buy</option><option value="SELL">Sell</option></select></div>
                  <div><label style={lbl}>Strike</label><select value={leg.strike_mode === 'ATM' && leg.strike_offset ? 'ATM' + (leg.strike_offset > 0 ? '+' : '') + leg.strike_offset : leg.strike_mode} onChange={function(e) { setLeg(idx,'strike_mode',e.target.value); setLeg(idx,'strike_offset',parseOffset(e.target.value)); }} style={inp}>{STRIKE_MODES.map(function(s) { return <option key={s.v} value={s.v}>{s.l}</option>; })}</select></div>
                  <div><label style={lbl}>Lots</label><input type="number" min="1" value={leg.qty_lots} onChange={function(e) { setLeg(idx,'qty_lots',+e.target.value); }} style={inp} /></div>
                </div>

                {/* Premium settings */}
                {leg.strike_mode === 'PREMIUM' && (
                  <div style={{ display:'grid', gridTemplateColumns:'120px 80px 120px', gap:8, marginBottom:8 }}>
                    <div><label style={lbl}>Premium Mode</label><select value={leg.premium_mode} onChange={function(e) { setLeg(idx,'premium_mode',e.target.value); }} style={inp}><option value="close_to">Close to</option><option value="higher_than">Higher than</option><option value="lower_than">Lower than</option></select></div>
                    <div><label style={lbl}>Value</label><input type="number" min="1" value={leg.premium_value} onChange={function(e) { setLeg(idx,'premium_value',+e.target.value); }} style={inp} /></div>
                    <div><label style={lbl}>Strike Filter</label><select value={leg.premium_strike_filter} onChange={function(e) { setLeg(idx,'premium_strike_filter',e.target.value); }} style={inp}><option value="none">All strikes</option><option value="100_multiple">100 multiple</option></select></div>
                  </div>
                )}

                {/* SL + Target per leg */}
                <div style={{ display:'grid', gridTemplateColumns:isCompact?'1fr 1fr':'1fr 1fr 1fr 1fr', gap:8 }}>
                  <div><label style={lbl}>SL Type</label><select value={leg.sl_type} onChange={function(e) { setLeg(idx,'sl_type',e.target.value); }} style={inp}><option value="none">None</option><option value="points">SL pts</option><option value="percentage">SL %</option></select></div>
                  {leg.sl_type !== 'none' && <div><label style={lbl}>SL Value</label><input type="number" min="0" value={leg.sl_value} onChange={function(e) { setLeg(idx,'sl_value',+e.target.value); }} style={inp} /></div>}
                  <div><label style={lbl}>Target</label><select value={leg.target_type} onChange={function(e) { setLeg(idx,'target_type',e.target.value); }} style={inp}><option value="none">None</option><option value="points">Tgt pts</option><option value="percentage">Tgt %</option></select></div>
                  {leg.target_type !== 'none' && <div><label style={lbl}>Tgt Value</label><input type="number" min="0" value={leg.target_value} onChange={function(e) { setLeg(idx,'target_value',+e.target.value); }} style={inp} /></div>}
                </div>

                {/* SL/Target action + Re-entry */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:8, marginTop:8 }}>
                  <div><label style={lbl}>On SL/Tgt Hit</label><select value={leg.square_off_action} onChange={function(e) { setLeg(idx,'square_off_action',e.target.value); }} style={inp}><option value="square_off_leg">Square off leg</option><option value="square_off_all">Square off ALL</option></select></div>
                  <div style={{ display:'flex', gap:4, alignItems:'flex-end' }}>
                    <input type="checkbox" checked={leg.re_entry_enabled} onChange={function(e) { setLeg(idx,'re_entry_enabled',e.target.checked); }} />
                    <span style={{ fontSize:11, color:'#8899aa' }}>Re-Entry</span>
                  </div>
                  {leg.re_entry_enabled && <div><label style={lbl}>Re-Entry Type</label><select value={leg.re_entry_type} onChange={function(e) { setLeg(idx,'re_entry_type',e.target.value); }} style={inp}><option value="RE_ENTRY">Re-Entry</option><option value="RE_COST">Re-Cost</option><option value="RE_EXECUTE">Re-Execute</option><option value="RE_ENTRY_REVERSE">Re-Entry Reverse</option><option value="RE_COST_REVERSE">Re-Cost Reverse</option><option value="RE_EXECUTE_REVERSE">Re-Execute Reverse</option></select></div>}
                  {leg.re_entry_enabled && <div><label style={lbl}>Max Re-entries</label><input type="number" min="0" max="20" value={leg.re_entry_max} onChange={function(e) { setLeg(idx,'re_entry_max',+e.target.value); }} style={inp} /></div>}
                </div>
              </div>
            );
          })}
        </div>

        {/* MTM TARGET / STOPLOSS */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
          <div style={{ padding:12, borderRadius:6, background:'#0d1526', border:'1px solid rgba(26,45,74,0.4)' }}>
            <div style={{ fontSize:11, fontWeight:800, color:'#00d4aa', marginBottom:8 }}>MTM TARGET</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              <div><label style={lbl}>Type</label><select value={algo.mtm_target_type} onChange={function(e) { setA('mtm_target_type')(e.target.value); }} style={inp}><option value="none">None</option><option value="points">Points</option><option value="percentage">Percentage</option><option value="rupees">Rupees</option></select></div>
              {algo.mtm_target_type !== 'none' && <div><label style={lbl}>Value</label><input type="number" min="0" value={algo.mtm_target_value} onChange={function(e) { setA('mtm_target_value')(+e.target.value); }} style={inp} /></div>}
            </div>
          </div>
          <div style={{ padding:12, borderRadius:6, background:'#0d1526', border:'1px solid rgba(26,45,74,0.4)' }}>
            <div style={{ fontSize:11, fontWeight:800, color:'#ff4466', marginBottom:8 }}>MTM STOPLOSS</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              <div><label style={lbl}>Type</label><select value={algo.mtm_sl_type} onChange={function(e) { setA('mtm_sl_type')(e.target.value); }} style={inp}><option value="none">None</option><option value="points">Points</option><option value="percentage">Percentage</option><option value="rupees">Rupees</option></select></div>
              {algo.mtm_sl_type !== 'none' && <div><label style={lbl}>Value</label><input type="number" min="0" value={algo.mtm_sl_value} onChange={function(e) { setA('mtm_sl_value')(+e.target.value); }} style={inp} /></div>}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:8 }}>
              <div><label style={lbl}>MTM Re-entry</label><select value={algo.mtm_re_entry} onChange={function(e) { setA('mtm_re_entry')(e.target.value); }} style={inp}><option value="none">None</option><option value="square_off_reenter">Square off & Re-enter</option><option value="reverse_reenter">Reverse & Re-enter</option></select></div>
              {algo.mtm_re_entry !== 'none' && <div><label style={lbl}>Max MTM Re-entries</label><input type="number" min="0" value={algo.mtm_re_entry_max} onChange={function(e) { setA('mtm_re_entry_max')(+e.target.value); }} style={inp} /></div>}
            </div>
          </div>
        </div>

        {/* ADVANCED SETTINGS */}
        <div style={{ padding:12, borderRadius:6, background:'#0d1526', border:'1px solid rgba(26,45,74,0.4)', marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:800, color:'#fbbf24', marginBottom:10 }}>ADVANCED SETTINGS</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:10 }}>
            <div><label style={lbl}>Entry Order Type</label><select value={algo.entry_order_type} onChange={function(e) { setA('entry_order_type')(e.target.value); }} style={inp}><option value="MARKET">Market (M)</option><option value="LIMIT">Limit</option><option value="SL_LIMIT">SL-Limit</option></select></div>
            <div><label style={lbl}>Exit Order Type</label><select value={algo.exit_order_type} onChange={function(e) { setA('exit_order_type')(e.target.value); }} style={inp}><option value="MARKET">Market (M)</option><option value="LIMIT">Limit</option><option value="SL_LIMIT">SL-Limit</option></select></div>
            <div><label style={lbl}>SL Buffer Type</label><select value={algo.sl_limit_buffer_type} onChange={function(e) { setA('sl_limit_buffer_type')(e.target.value); }} style={inp}><option value="percentage">Percentage</option><option value="points">Points</option></select></div>
            <div><label style={lbl}>Buffer Value</label><input type="number" min="0" step="0.5" value={algo.sl_limit_buffer_value} onChange={function(e) { setA('sl_limit_buffer_value')(+e.target.value); }} style={inp} /></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:10, marginTop:10 }}>
            <div><label style={lbl}>SL Placement</label><select value={algo.sl_placement} onChange={function(e) { setA('sl_placement')(e.target.value); }} style={inp}><option value="place_in_advance">Place in advance</option><option value="place_after_delay">Place after delay</option></select></div>
            <div><label style={lbl}>SL Delay (leg) sec</label><input type="number" min="0" max="60" value={algo.sl_placement_delay_leg} onChange={function(e) { setA('sl_placement_delay_leg')(+e.target.value); }} style={inp} /></div>
            <div><label style={lbl}>SL Delay (MTM) sec</label><input type="number" min="0" max="60" value={algo.sl_placement_delay_mtm} onChange={function(e) { setA('sl_placement_delay_mtm')(+e.target.value); }} style={inp} /></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:10, marginTop:10 }}>
            <div><label style={lbl}>Re-Execute Method</label><select value={algo.re_execute_method} onChange={function(e) { setA('re_execute_method')(e.target.value); }} style={inp}><option value="LTP">LTP (Immediate)</option><option value="candle_close">Candle Close</option></select></div>
            <div><label style={lbl}>Re-Cost Activation</label><select value={algo.re_cost_activation} onChange={function(e) { setA('re_cost_activation')(e.target.value); }} style={inp}><option value="next_minute">Next Minute</option><option value="immediate">Immediate</option></select></div>
            <div><label style={lbl}>Trailing Freq (min)</label><input type="number" min="1" max="60" value={algo.trailing_frequency_mins} onChange={function(e) { setA('trailing_frequency_mins')(+e.target.value); }} style={inp} /></div>
            <div style={{ display:'flex', gap:6, alignItems:'flex-end' }}>
              <div><input type="checkbox" checked={algo.auto_exit_margin_error} onChange={function(e) { setA('auto_exit_margin_error')(e.target.checked); }} /><span style={{ fontSize:11, color:'#8899aa', marginLeft:4 }}>Auto exit on margin error</span></div>
            </div>
          </div>
        </div>

        {/* Validation errors */}
        {saveErrors.length > 0 && (
          <div style={{ padding:'10px 14px', borderRadius:6, background:'rgba(255,68,102,0.08)', border:'1px solid rgba(255,68,102,0.25)', marginBottom:10 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#ff4466', marginBottom:6 }}>{'\u26A0'} Fix before saving:</div>
            {saveErrors.map(function(e, i) {
              return <div key={i} style={{ fontSize:11, color:'#ff8888', marginBottom:2, paddingLeft:12 }}>{'\u2022'} {e}</div>;
            })}
          </div>
        )}

        {/* Save buttons */}
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={saveAlgo} disabled={loading} style={btn('#00d4aa', 'rgba(0,212,170,0.15)')}>{loading ? 'Saving...' : 'Save'}</button>
          <button onClick={function() { saveAlgo(); }} disabled={loading} style={btn('#0ea5e9', 'rgba(14,165,233,0.15)')}>Save & Go Back</button>
          <button onClick={function() { setView('list'); setEditAlgo(null); setSaveErrors([]); }} style={btn('#8899aa', 'rgba(100,100,120,0.1)')}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
